// Multi-leg route form for the charter webchat — the "route form is the center" surface.
//
// Renders the WHOLE itinerary as an ordered list of legs; each leg is Origin -> Destination with its
// own date + time. The rider can add a leg, remove a leg, reorder legs, and edit any address/city/
// date/time in place — so a misunderstood itinerary is fixed here instead of re-explained in chat.
//
// On Confirm it calls onConfirm(routeText, token): `routeText` is a clean verified sentence sent as the
// rider's next turn, and `token` is the { legs: [...] } payload CustomerApp appends as
// `[[ROUTE={<compact-json>}]]`. The agents side reads it via _route_verified_slots (the legs[] branch)
// -> merge_verified_itinerary (the authoritative add/remove/reorder replace). buildMultiLegToken's
// field names (legs[].leg/pickup/destination/stops/departDateTime; place address/city/state/isAirport/
// iata) are the FROZEN web->agents contract — do NOT rename.
//
// Reuses the single place search (PlaceField) + token/phrase helpers from RoutePopup, so endpoint and
// stop resolution behaves identically everywhere.
//
// VARIANTS (both single-leg by definition — no add/remove-leg UI):
//   oneWay  — one leg, no round-trip toggle; everything else identical to the generic form.
//   shuttle — the recurring-shuttle loop: one leg relabelled (Shuttle pickup point / Shuttle drop-off
//             point), the leg's date/time shown as "First service day & first pickup time" (the agents
//             map the confirmed departDateTime into shuttle_start_date / shuttle_out_time), and the
//             stops editor HIDDEN — a shuttle's extra pickup spots are chat-collected
//             (sh_extra_pickups), so showing "Stops along the way" here would double-collect them with
//             the wrong vocabulary. Endpoints are the SAME LegEndpoint/PlaceField as one-way, so the
//             chat-named-place prefill cascade (auto-fill vs offer vs fallback) and the coarse-label
//             token rules apply identically. Confirm emits the same single-leg legs[] token
//             (buildMultiLegToken) the shuttle merge path already accepts — no new keys.

import React, { useState } from 'react';
import { Plus, X, ChevronUp, ChevronDown } from 'lucide-react';
import { Modal } from '../ui/Modal';
import { Button } from '../ui/primitives';
import { cx } from '../ui/cx';
import { PlaceField, tokenPlace, placePhrase } from './RoutePopup';
import { stateInvalid, duplicateStopIndices, dedupeStops } from './validation';

// Exported so the dedicated round-trip form (RouteFormRoundTrip) reuses the exact same input styling,
// endpoint UI, and date/time helpers — one visual + behavioral system across both forms.
export const INPUT_BASE =
  'w-full rounded-btn border border-peoria-border bg-peoria-surface2 text-sm text-peoria-text placeholder:text-peoria-muted px-3 py-2 ' +
  'transition-colors duration-150 focus:outline-none focus:ring-2 focus:ring-peoria-red/30 focus:border-peoria-red';

// today's date (local) as YYYY-MM-DD, for the date picker's future-only `min`.
export function todayISO() {
  const d = new Date();
  const off = d.getTimezoneOffset();
  return new Date(d.getTime() - off * 60000).toISOString().slice(0, 10);
}

// "2026-07-15" + "14:30" -> "2026-07-15T14:30:00". Date only -> "2026-07-15" (the agents backstop
// re-asks the time). No date -> "".
export function combineDateTime(date, time) {
  const d = (date || '').trim();
  if (!d) return '';
  const t = (time || '').trim();
  if (!t) return d;
  return `${d}T${t.length === 5 ? `${t}:00` : t}`;
}

function blankLeg() {
  return { from: null, to: null, departDate: '', departTime: '', stops: [],
           roundTrip: false, returnDate: '', returnTime: '' };
}

// Builds the { legs: [...] } reverse-route token payload for the confirm. Pure + exported so it's
// unit-testable. Each leg carries its 1-based position, verified endpoints, its own datetime, and any
// stops. A round-trip leg (the "return to origin" toggle) also carries `returnDateTime` — the agents map
// a leg-1 returnDateTime -> return_datetime, so the trip stays ONE round-trip quote (not two one-ways).
export function buildMultiLegToken(legs) {
  return {
    legs: (legs || []).map((leg, i) => {
      const out = {
        leg: i + 1,
        pickup: tokenPlace(leg.from || {}),
        destination: tokenPlace(leg.to || {}),
        // Drop empty (unpicked) slots AND duplicate stops so a repeated stop never reaches the agents.
        // keepCoarseLabel: a stop is carried by its address alone (blank => the agents drop it).
        stops: dedupeStops((leg.stops || []).filter(Boolean)).map((s) => tokenPlace(s, { keepCoarseLabel: true })),
        departDateTime: combineDateTime(leg.departDate, leg.departTime),
      };
      if (leg.roundTrip && leg.returnDate) {
        out.returnDateTime = combineDateTime(leg.returnDate, leg.returnTime);
      }
      return out;
    }),
  };
}

// A blank coarse (city-level) place — has no `precision`, so isSpecificEnough() fails OPEN and a
// city-only endpoint is still confirmable for a preliminary estimate (per the "don't know the address
// yet" flow). address='' -> tokenPlace emits a blank address the agents pend.
export const coarsePlace = (city = '', state = '') => ({
  label: '', city, state, isAirport: false, iataCode: null,
});
// An endpoint counts as entered once it has EITHER a resolved address (label) or at least a city.
export const hasEndpoint = (p) => !!p && (!!p.label || !!p.city);

// A required-field marker: a red asterisk after a label. Optional fields render no marker.
export const Req = () => <span className="text-danger" aria-hidden="true"> *</span>;

// One endpoint (From / To): a SINGLE place search.
//
// Was four inputs — City, ST, Location name, Exact address — which existed only because ORS/Pelias
// couldn't resolve a venue name, so the rider supplied the city/state by hand and the venue name was
// kept as separate free text. Google Places returns all of it from one pick, so `PlaceField` owns the
// whole endpoint: search → resolved result line, with a City + ST escape hatch it reveals itself when
// Google can't place something, and prefill from the name the chat already captured.
//
// `onPickCityState` and `onName` are gone: one pick supplies the venue name AND the city/state, so
// there is nothing left for a caller to patch separately. `onCity`/`onState` remain for the manual
// escape hatch PlaceField reveals when Google can't place something.
export function LegEndpoint({ legNum, label, ariaTag, place, onCity, onState, onSelectAddr, onClearAddr }) {
  return (
    <PlaceField
      label={label}
      ariaTag={ariaTag || `Leg ${legNum} ${label}`}
      place={place}
      onSelect={onSelectAddr}
      onClear={onClearAddr}
      onCity={onCity}
      onState={onState}
    />
  );
}

export default function RouteFormMulti({ isOpen, onClose, onConfirm, initialLegs, oneWay = false,
                                         shuttle = false }) {
  const [legs, setLegs] = useState(() =>
    Array.isArray(initialLegs) && initialLegs.length ? initialLegs.map((l) => ({ ...blankLeg(), ...l })) : [blankLeg()]);
  // A one-way trip and a shuttle loop are single-leg BY DEFINITION — no add-leg, no reorder/remove,
  // and (shuttle) no round-trip toggle either.
  const single = oneWay || shuttle;
  // A MULTI-LEG trip (seeded with 2+ legs, not one-way/shuttle) must keep at least TWO legs — deleting
  // down to one would make the agents side re-derive the trip type away from multi_leg
  // (merge_verified_itinerary) and break the dedicated multi-leg flow. A single-seeded generic/one-way
  // form keeps the single-leg minimum (and its round-trip-to-origin toggle).
  const minLegs = (!single
                   && Array.isArray(initialLegs) && initialLegs.length >= 2) ? 2 : 1;

  const setLeg = (i, patch) => setLegs((ls) => ls.map((l, idx) => (idx === i ? { ...l, ...patch } : l)));
  // Merge a Location-tier edit (city / state) into an endpoint's place, preserving any resolved address.
  const patchLoc = (i, side, patch) => setLegs((ls) => ls.map((l, idx) => {
    if (idx !== i) return l;
    const base = l[side] || coarsePlace();
    return { ...l, [side]: { ...base, ...patch } };
  }));
  // Per-leg STOPS (intermediate stops along a leg) — a new slot starts null (empty) and is filled once
  // an ORS candidate is picked. Empty slots are dropped when the token is built (buildMultiLegToken).
  const addStop = (i) => setLegs((ls) => ls.map((l, idx) =>
    (idx === i ? { ...l, stops: [...(l.stops || []), null] } : l)));
  const setStopAt = (i, j, place) => setLegs((ls) => ls.map((l, idx) =>
    (idx === i ? { ...l, stops: (l.stops || []).map((s, k) => (k === j ? place : s)) } : l)));
  const removeStop = (i, j) => setLegs((ls) => ls.map((l, idx) =>
    (idx === i ? { ...l, stops: (l.stops || []).filter((_, k) => k !== j) } : l)));
  const addLeg = () => setLegs((ls) => [...ls, blankLeg()]);
  const removeLeg = (i) => setLegs((ls) => (ls.length <= minLegs ? ls : ls.filter((_, idx) => idx !== i)));
  const moveLeg = (i, dir) => setLegs((ls) => {
    const j = i + dir;
    if (j < 0 || j >= ls.length) return ls;
    const next = ls.slice();
    [next[i], next[j]] = [next[j], next[i]];
    return next;
  });

  // A leg is confirmable when both endpoints are entered, it has a date, every typed state is a real
  // code, and no stop repeats another on the same leg.
  const legStatesOk = (l) => !stateInvalid(l.from?.state) && !stateInvalid(l.to?.state)
    && (l.stops || []).every((s) => !s || !stateInvalid(s.state));
  const legOk = (l) => hasEndpoint(l.from) && hasEndpoint(l.to) && !!l.departDate
    && legStatesOk(l) && duplicateStopIndices((l.stops || []).filter(Boolean)).size === 0;
  const canConfirm = legs.length >= minLegs && legs.every(legOk);

  // placePhrase on a city-only (empty-label) place would prefix a stray ", " — trim it for the sentence.
  const phraseOf = (p) => (p ? placePhrase(p).replace(/^,\s*/, '') : '');
  const confirm = () => {
    if (!canConfirm) return;
    // A stop's phrase: its venue name and/or city — so the confirmed itinerary sentence reads
    // "Bettendorf, IA (via Riverside Park, Bettendorf, IA) → Moline, IL".
    const stopPhrase = (s) => {
      if (!s) return '';
      const name = String(s.name || '').trim();
      const cs = [s.city, s.state].filter(Boolean).join(', ');
      return (name && cs) ? `${name}, ${cs}` : (name || cs || String(s.label || '').trim());
    };
    const legPhrase = (l) => {
      const stops = (l.stops || []).map(stopPhrase).filter(Boolean);
      const via = stops.length ? ` (via ${stops.join(', ')})` : '';
      return `${phraseOf(l.from)}${via} → ${phraseOf(l.to)}`;
    };
    const chain = legs.map(legPhrase).join('; then ');
    const rt = (legs.length === 1 && legs[0].roundTrip && legs[0].returnDate)
      ? ` (round trip, returning ${legs[0].returnDate}${legs[0].returnTime ? ` at ${legs[0].returnTime}` : ''})`
      : '';
    // The shuttle confirm speaks the shuttle flow's own vocabulary (pickup point / drop-off point /
    // first service day) — the same sentence shape the legacy shuttle modal sent, so the agents-side
    // extraction reads an unambiguous loop, never a point-to-point itinerary.
    const routeText = shuttle
      ? `My shuttle route — pickup point ${phraseOf(legs[0].from)}; drop-off point ${phraseOf(legs[0].to)}`
        + `; first service day ${legs[0].departDate}${legs[0].departTime ? ` at ${legs[0].departTime}` : ''}.`
      : `My charter itinerary — ${chain}${rt}.`;
    // Pass the RAW legs so the caller can both build the token (buildMultiLegToken) AND re-seed the form
    // on a later edit (the token is lossy — it drops split date/time fields).
    onConfirm(routeText, legs);
  };

  const footer = (
    <>
      <Button variant="peoriaOutline" onClick={onClose}>Cancel</Button>
      <Button variant="peoria" onClick={confirm} disabled={!canConfirm}>
        Confirm itinerary
      </Button>
    </>
  );

  return (
    <Modal open={isOpen} onClose={onClose} title={shuttle ? 'Your shuttle route' : 'Your itinerary'}
      size="wide" footer={footer} tone="peoria">
      {/* Preliminary-estimate note: encourage the rider to keep going even without exact addresses. */}
      <div className="mb-5 rounded-card border border-peoria-red/30 bg-peoria-red/10 px-4 py-3 text-13 text-peoria-muted">
        <strong className="text-peoria-text">Don't know the exact address yet? No problem.</strong>{' '}
        The city and state are enough for a preliminary estimate — start typing the exact address when
        you have it and we'll fill in the rest. You can update any of this later.
      </div>

      <ol className="space-y-5">
        {legs.map((leg, i) => (
          <li key={i} className="rounded-card border border-peoria-border bg-peoria-surface2/40 p-4 sm:p-5">
            {/* The shuttle loop is exactly ONE leg — no "Leg 1" chrome, no reorder/remove controls. */}
            {!shuttle && (
            <div className="mb-4 flex items-center justify-between">
              <span className="text-11 font-semibold uppercase tracking-brand text-peoria-red">Leg {i + 1}</span>
              <div className="flex items-center gap-0.5">
                <button type="button" title="Move up" aria-label={`Move leg ${i + 1} up`}
                  onClick={() => moveLeg(i, -1)} disabled={i === 0}
                  className="rounded-btn p-1 text-peoria-muted hover:bg-peoria-surface2 hover:text-peoria-text disabled:opacity-30">
                  <ChevronUp size={16} />
                </button>
                <button type="button" title="Move down" aria-label={`Move leg ${i + 1} down`}
                  onClick={() => moveLeg(i, 1)} disabled={i === legs.length - 1}
                  className="rounded-btn p-1 text-peoria-muted hover:bg-peoria-surface2 hover:text-peoria-text disabled:opacity-30">
                  <ChevronDown size={16} />
                </button>
                <button type="button" title="Remove leg" aria-label={`Remove leg ${i + 1}`}
                  onClick={() => removeLeg(i)} disabled={legs.length <= minLegs}
                  className="rounded-btn p-1 text-peoria-muted hover:bg-peoria-surface2 hover:text-danger disabled:opacity-30">
                  <X size={16} />
                </button>
              </div>
            </div>
            )}
            {/* From / To side-by-side on a wide viewport — the whole point of the roomier workspace.

                A picked place REPLACES the endpoint outright: one search supplies the venue name, the
                street, the city and the state together. Carrying a previous `name` forward — which the
                old four-input endpoint HAD to do, so picking an address wouldn't wipe the separately
                typed Location name — now strands a stale venue over a different address: picking
                "2360 95th St" kept "Patel Brothers", a mile away. Likewise a cleared box empties the
                whole endpoint; with one field, an empty box still claiming "Naperville, IL" underneath
                was just confusing. */}
            <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
              <LegEndpoint
                legNum={i + 1}
                label={shuttle ? 'Shuttle pickup point' : 'From'}
                ariaTag={shuttle ? 'Shuttle pickup point' : undefined}
                place={leg.from}
                onCity={(v) => patchLoc(i, 'from', { city: v })}
                onState={(v) => patchLoc(i, 'from', { state: v.toUpperCase().slice(0, 2) })}
                onSelectAddr={(p) => setLeg(i, { from: { ...p } })}
                onClearAddr={() => setLeg(i, { from: null })}
              />
              <LegEndpoint
                legNum={i + 1}
                label={shuttle ? 'Shuttle drop-off point' : 'To'}
                ariaTag={shuttle ? 'Shuttle drop-off point' : undefined}
                place={leg.to}
                onCity={(v) => patchLoc(i, 'to', { city: v })}
                onState={(v) => patchLoc(i, 'to', { state: v.toUpperCase().slice(0, 2) })}
                onSelectAddr={(p) => setLeg(i, { to: { ...p } })}
                onClearAddr={() => setLeg(i, { to: null })}
              />
            </div>
            {/* Stops along THIS leg (optional) — each an ORS type-ahead; empty rows drop on confirm.
                HIDDEN for a shuttle: its extra pickup spots are chat-collected (sh_extra_pickups) and
                its token carries no stops — a stops editor here would double-collect them with the
                wrong vocabulary. */}
            {!shuttle && (
            <div className="mt-5">
              <div className="mb-1.5 text-10 font-semibold uppercase tracking-brand text-peoria-muted">
                Stops along the way (optional)
              </div>
              {(leg.stops || []).length > 0 && (
                <div className="space-y-3">
                  {(leg.stops || []).map((stop, j) => (
                    <div key={j} className="flex items-start gap-2">
                      <div className="flex-1">
                        {stop && stop.descriptive ? (
                          // A DESCRIPTIVE stop the customer named by type ("Lunch stop", "a gas station"):
                          // shown in their words as a plain editable row, with a way to make it a specific
                          // place (which swaps in the resolving type-ahead).
                          <label className="block space-y-1.5">
                            <div className="text-sm font-medium text-peoria-text">Stop {j + 1}</div>
                            <input
                              className={cx(INPUT_BASE)}
                              placeholder="e.g. lunch stop, gas station"
                              value={stop.label || ''}
                              aria-label={`Leg ${i + 1} stop ${j + 1} description`}
                              onChange={(e) => setStopAt(i, j, { label: e.target.value, descriptive: true })}
                            />
                            <button type="button" onClick={() => setStopAt(i, j, null)}
                              className="text-11 text-peoria-red underline hover:opacity-80">
                              Make this a specific place
                            </button>
                          </label>
                        ) : (
                          // A specific stop — the SAME single search as From/To, so the form reads as
                          // one system. It carried the same stale-name handler the endpoints did
                          // (preferring an existing `name` over the picked place's), which stranded a
                          // venue over an unrelated address; picking now replaces the stop outright.
                          <PlaceField
                            label={`Stop ${j + 1}`}
                            required={false}
                            ariaTag={`Leg ${i + 1} stop ${j + 1}`}
                            place={stop}
                            placeholder="A stop along the way"
                            onSelect={(p) => setStopAt(i, j, { ...p })}
                            onClear={() => setStopAt(i, j, null)}
                            onCity={(v) => setStopAt(i, j, { ...(stop || {}), city: v })}
                            onState={(v) => setStopAt(i, j, { ...(stop || {}), state: v.toUpperCase().slice(0, 2) })}
                          />
                        )}
                        {duplicateStopIndices(leg.stops).has(j) && (
                          <div className="mt-1 text-11 text-danger">Duplicate stop — it won't be added twice.</div>
                        )}
                      </div>
                      <button type="button" onClick={() => removeStop(i, j)}
                        aria-label={`Remove leg ${i + 1} stop ${j + 1}`}
                        className="mt-7 rounded-btn p-1 text-peoria-muted hover:bg-peoria-surface2 hover:text-danger">
                        <X size={16} />
                      </button>
                    </div>
                  ))}
                </div>
              )}
              <button type="button" onClick={() => addStop(i)}
                aria-label={`Add a stop to leg ${i + 1}`}
                className="mt-2 inline-flex items-center gap-1 text-13 font-medium text-peoria-red hover:opacity-80">
                <Plus size={14} /> Add stop
              </button>
            </div>
            )}
            {/* SHUTTLE: this leg's date/time IS the loop's start — "First service day & first pickup
                time" (the agents map departDateTime into shuttle_start_date / shuttle_out_time). The
                date stays required (legOk); the time stays optional (the chat backstop re-asks it). */}
            {shuttle && (
              <div className="mt-5 mb-1.5 text-10 font-semibold uppercase tracking-brand text-peoria-muted">
                First service day &amp; first pickup time
              </div>
            )}
            <div className={cx('grid grid-cols-2 gap-3 md:max-w-md', shuttle ? 'mt-1' : 'mt-5')}>
              <label className="block space-y-1.5">
                <span className="text-sm font-medium text-peoria-text">
                  {shuttle ? 'First service day' : 'Departure date'}<Req />
                </span>
                <input type="date" className={INPUT_BASE} min={todayISO()} value={leg.departDate}
                  aria-label={shuttle ? 'First service day' : `Leg ${i + 1} date`}
                  onChange={(e) => setLeg(i, { departDate: e.target.value })} />
              </label>
              <label className="block space-y-1.5">
                <span className="text-sm font-medium text-peoria-text">
                  {shuttle ? 'First pickup time' : 'Departure time'}
                </span>
                <input type="time" className={INPUT_BASE} value={leg.departTime}
                  aria-label={shuttle ? 'First pickup time' : `Leg ${i + 1} time`}
                  onChange={(e) => setLeg(i, { departTime: e.target.value })} />
              </label>
            </div>
            {/* ROUND TRIP: a single-leg trip can be a round trip — one leg + a "return to origin" toggle
                with its own date/time. Shown only when there's ONE leg (a multi-leg itinerary lists each
                leg explicitly instead). The ride-home route is the reverse. */}
            {!single && legs.length === 1 && (
              <div className="mt-4 rounded-card border border-peoria-border bg-peoria-surface2/40 p-3">
                <label className="flex items-center gap-2 text-sm font-medium text-peoria-text">
                  <input type="checkbox" className="h-4 w-4 rounded border-peoria-border text-peoria-red focus:ring-peoria-red/30"
                    aria-label="Round trip — return to origin"
                    checked={!!leg.roundTrip}
                    onChange={(e) => setLeg(i, { roundTrip: e.target.checked })} />
                  Round trip — return to {phraseOf(leg.from) || 'the pickup'}
                </label>
                {leg.roundTrip && (
                  <div className="mt-3 grid grid-cols-2 gap-3 md:max-w-md">
                    <label className="block space-y-1.5">
                      <span className="text-sm font-medium text-peoria-text">Return date</span>
                      <input type="date" className={INPUT_BASE} min={leg.departDate || todayISO()}
                        aria-label="Return date" value={leg.returnDate}
                        onChange={(e) => setLeg(i, { returnDate: e.target.value })} />
                    </label>
                    <label className="block space-y-1.5">
                      <span className="text-sm font-medium text-peoria-text">Return time</span>
                      <input type="time" className={INPUT_BASE} aria-label="Return time" value={leg.returnTime}
                        onChange={(e) => setLeg(i, { returnTime: e.target.value })} />
                    </label>
                  </div>
                )}
              </div>
            )}
          </li>
        ))}
      </ol>

      {/* A one-way trip / shuttle loop is a single leg by definition — no "add another leg". */}
      {!single && (
        <button type="button" onClick={addLeg}
          className={cx('mt-4 flex w-full items-center justify-center gap-1.5 rounded-card border border-dashed',
            'border-peoria-border py-3 text-13 font-semibold text-peoria-red hover:border-peoria-red hover:bg-peoria-red/10')}>
          <Plus size={16} /> Add another leg
        </button>
      )}
    </Modal>
  );
}
